import { DatePipe, NgFor, NgIf } from '@angular/common';
import { Component, effect, inject, input, output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageModule } from 'primeng/message';
import { ApiService } from 'src/app/core/services/api.service';
import { BelenderPortalItem } from 'src/app/models/BelenderPortalResponse';
import { OCCASIONAL_FAIL, OK, PERSISTENT_FAIL } from '../../constants/belender-health.constants';

interface StatusBehavior {
    severity: 'error' | 'warn';
    available: boolean;
}

interface HealthCheckDef {
    endpoint: string;
    translationKey: string;
    affectsIsOk: boolean;
    atLeastOneServiceOk: boolean;
    statusBehavior: {
        persistentFail: StatusBehavior;
        occasionalFail: StatusBehavior;
        default: StatusBehavior;
    };
}

interface CrossCheckRule {
    conditions: { endpoint: string; status: string }[];
    result: { isOk: boolean };
    overrideSeverity?: 'error' | 'warn';
}

interface HealthCheckConfig {
    checks: HealthCheckDef[];
    crossCheckRules?: CrossCheckRule[];
}

export type AdminMessagesType = 'clave' | 'clavePermissive' | 'sms' | 'claveCards' | 'clientCertificate' | 'clientCertificatePermissive';

interface AdminMessageItem {
    severity: 'error' | 'warn' | 'success' | 'info';
    summary: string;
    detail: string;
    data?: string[];
}

const CLAVE_BASE: HealthCheckDef[] = [
    {
        endpoint: 'admin-health/belender/sms',
        translationKey: 'HEALTH_CHECK_SMS',
        affectsIsOk: true,
        atLeastOneServiceOk: false,
        statusBehavior: {
            persistentFail: { severity: 'error', available: false },
            occasionalFail: { severity: 'warn', available: false },
            default: { severity: 'warn', available: false }
        }
    },
    {
        endpoint: 'admin-health/belender/clave-pin',
        translationKey: 'HEALTH_CHECK_CLAVE_PIN',
        affectsIsOk: true,
        atLeastOneServiceOk: false,
        statusBehavior: {
            persistentFail: { severity: 'error', available: false },
            occasionalFail: { severity: 'warn', available: false },
            default: { severity: 'warn', available: false }
        }
    }
];

const CLIENT_CERTIFICATE_BASE: HealthCheckDef[] = [
    {
        endpoint: 'admin-health/belender/client-certificate',
        translationKey: 'HEALTH_CHECK_CLIENT_CERTIFICATE',
        affectsIsOk: true,
        atLeastOneServiceOk: false,
        statusBehavior: {
            persistentFail: { severity: 'error', available: false },
            occasionalFail: { severity: 'warn', available: false },
            default: { severity: 'warn', available: false }
        }
    },
    {
        endpoint: 'admin-health/belender/video-id',
        translationKey: 'HEALTH_CHECK_VIDEO_ID',
        affectsIsOk: false,
        atLeastOneServiceOk: false,
        statusBehavior: {
            persistentFail: { severity: 'error', available: false },
            occasionalFail: { severity: 'warn', available: false },
            default: { severity: 'warn', available: false }
        }
    },
];

const SMS_BASE: HealthCheckDef[] = [
    {
        endpoint: 'admin-health/belender/sms',
        translationKey: 'HEALTH_CHECK_SMS',
        affectsIsOk: true,
        atLeastOneServiceOk: false,
        statusBehavior: {
            persistentFail: { severity: 'error', available: false },
            occasionalFail: { severity: 'warn', available: true },
            default: { severity: 'warn', available: true }
        }
    },
    {
        endpoint: 'admin-health/belender/clave-pin',
        translationKey: 'HEALTH_CHECK_CLAVE_PIN',
        affectsIsOk: false,
        atLeastOneServiceOk: false,
        statusBehavior: {
            persistentFail: { severity: 'warn', available: false },
            occasionalFail: { severity: 'warn', available: false },
            default: { severity: 'warn', available: false }
        }
    }
];

type StatusBehaviorKey = 'persistentFail' | 'occasionalFail' | 'default';

function withAvailableOverrides(
    base: HealthCheckDef[],
    overrides: Record<string, Partial<Record<StatusBehaviorKey, boolean>>>
): HealthCheckDef[] {
    return base.map(check => {
        const endpointOverrides = overrides[check.endpoint];
        if (!endpointOverrides) return check;

        const statusBehavior = { ...check.statusBehavior };
        for (const key of Object.keys(endpointOverrides) as StatusBehaviorKey[]) {
            statusBehavior[key] = { ...statusBehavior[key], available: endpointOverrides[key]! };
        }
        return { ...check, statusBehavior };
    });
}

const HEALTH_CHECK_CONFIGS: Record<AdminMessagesType, HealthCheckConfig> = {
    clave: { checks: CLAVE_BASE },
    clavePermissive: {
        checks: withAvailableOverrides(CLAVE_BASE, {
            'admin-health/belender/sms': { occasionalFail: true, default: true },
            'admin-health/belender/clave-pin': { occasionalFail: true, default: true }
        })
    },
    sms: { checks: SMS_BASE },
    claveCards: {
        checks: [
            {
                endpoint: 'admin-health/belender/sms',
                translationKey: 'HEALTH_CHECK_SMS',
                affectsIsOk: false,
                atLeastOneServiceOk: true,
                statusBehavior: {
                    persistentFail: { severity: 'warn', available: false },
                    occasionalFail: { severity: 'warn', available: true },
                    default: { severity: 'warn', available: true }
                }
            },
            {
                endpoint: 'admin-health/belender/clave-pin',
                translationKey: 'HEALTH_CHECK_CLAVE_PIN',
                affectsIsOk: false,
                atLeastOneServiceOk: true,
                statusBehavior: {
                    persistentFail: { severity: 'warn', available: false },
                    occasionalFail: { severity: 'warn', available: true },
                    default: { severity: 'warn', available: true }
                }
            }
        ],
        crossCheckRules: [
            {
                conditions: [
                    { endpoint: 'admin-health/belender/clave-pin', status: PERSISTENT_FAIL },
                    { endpoint: 'admin-health/belender/sms', status: PERSISTENT_FAIL }
                ],
                result: { isOk: false },
                overrideSeverity: 'error'
            }
        ]
    },
    clientCertificate: { checks: CLIENT_CERTIFICATE_BASE },
    clientCertificatePermissive: {
        checks: withAvailableOverrides(CLIENT_CERTIFICATE_BASE, {
            'admin-health/belender/client-certificate': { occasionalFail: true, default: true },
            'admin-health/belender/video-id': { occasionalFail: true, default: true }
        })
    }
};

@Component({
    selector: 'app-admin-messages',
    standalone: true,
    imports: [MessageModule, NgIf, NgFor],
    templateUrl: './admin-messages.component.html'
})
export class AdminMessagesComponent {
    private readonly api = inject(ApiService);
    private readonly datePipe = inject(DatePipe);
    private readonly translate = inject(TranslateService);

    protected readonly closable = false;
    protected messages: AdminMessageItem[] | undefined;

    type = input.required<AdminMessagesType>();
    isOk = output<boolean>();

    constructor() {
        effect(async () => await this.setMessages());
    }

    async setMessages(): Promise<void> {
        const type = this.type();

        this.messages = undefined;
        this.isOk.emit(true);

        const config = HEALTH_CHECK_CONFIGS[type];
        if (!config) return;

        await this.processHealthChecks(config);
    }

    private async processHealthChecks(config: HealthCheckConfig): Promise<void> {
        const { checks, crossCheckRules } = config;
        const responses = await Promise.all(checks.map(check => this.api.get<BelenderPortalItem>(check.endpoint)));

        const messages: AdminMessageItem[] = [];
        const messageEndpoints: string[] = [];
        const messagesAtLeastOneServiceOk: AdminMessageItem[] = [];
        const messageEndpointsAtLeastOneServiceOk: string[] = [];
        let isOk = true;
        let hasToCheckAtLeastOneServiceOk = false;
        let checkAtLeastOneServiceOk = false;
        responses.forEach((res, index) => {
            const check = checks[index];
            const updatedAt = this.datePipe.transform(res.updated_at, 'dd/MM/yyyy HH:mm:ss');

            if(check.atLeastOneServiceOk){
                hasToCheckAtLeastOneServiceOk = true;
            }

            if (res.status === OK) {
                if (check.atLeastOneServiceOk) {
                    checkAtLeastOneServiceOk = true;
                }
                return;
            }
            else if(check.atLeastOneServiceOk){
                const behavior = this.getStatusBehavior(res.status, check.statusBehavior);
                const translationSuffix = this.getTranslationSuffix(res.status);
                const detail = this.translate.instant(`NEW_REQUEST.${check.translationKey}.${translationSuffix}`, { date: updatedAt });

                messagesAtLeastOneServiceOk.push({ severity: behavior.severity, summary: '', detail });
                messageEndpointsAtLeastOneServiceOk.push(check.endpoint);

                if (behavior.available) {
                    checkAtLeastOneServiceOk = checkAtLeastOneServiceOk || true;
                }
            }
            else{
                const behavior = this.getStatusBehavior(res.status, check.statusBehavior);
                const translationSuffix = this.getTranslationSuffix(res.status);
                const detail = this.translate.instant(`NEW_REQUEST.${check.translationKey}.${translationSuffix}`, { date: updatedAt });

                messages.push({ severity: behavior.severity, summary: '', detail });
                messageEndpoints.push(check.endpoint);

                if (check.affectsIsOk && !behavior.available) {
                    isOk = false;
                }
            }
        });

        if(isOk && hasToCheckAtLeastOneServiceOk){
            isOk = checkAtLeastOneServiceOk;
            if(isOk){
                //copio todo el array de messagesAtLeastOneServiceOk y messageEndpointsAtLeastOneServiceOk sobre messages y messageEndpoints
                messages.push(...messagesAtLeastOneServiceOk);
                messageEndpoints.push(...messageEndpointsAtLeastOneServiceOk);
            }
            else{
                //copio todo el array de messagesAtLeastOneServiceOk y messageEndpointsAtLeastOneServiceOk sobre messages y messageEndpoints cambiando la propiedad severity de warn a error para todos los elementos del array messagesAtLeastOneServiceOk
                messagesAtLeastOneServiceOk.forEach(msg => msg.severity = 'error');
                messages.push(...messagesAtLeastOneServiceOk);
                messageEndpoints.push(...messageEndpointsAtLeastOneServiceOk);
            }
        }

        if (isOk && crossCheckRules?.length) {
            const statusByEndpoint = new Map<string, string>();
            responses.forEach((res, index) => {
                statusByEndpoint.set(checks[index].endpoint, res.status);
            });

            for (const rule of crossCheckRules) {
                const allConditionsMet = rule.conditions.every(
                    condition => statusByEndpoint.get(condition.endpoint) === condition.status
                );
                if (allConditionsMet) {
                    isOk = rule.result.isOk;

                    if (rule.overrideSeverity) {
                        const affectedEndpoints = new Set(rule.conditions.map(c => c.endpoint));
                        messages.forEach((msg, i) => {
                            if (affectedEndpoints.has(messageEndpoints[i])) {
                                msg.severity = rule.overrideSeverity;
                            }
                        });
                    }

                    break;
                }
            }
        }

        this.messages = messages.length > 0 ? messages : undefined;
        this.isOk.emit(isOk);
    }

    private getStatusBehavior(status: string, config: HealthCheckDef['statusBehavior']): StatusBehavior {
        switch (status) {
            case PERSISTENT_FAIL: return config.persistentFail;
            case OCCASIONAL_FAIL: return config.occasionalFail;
            default: return config.default;
        }
    }

    private getTranslationSuffix(status: string): string {
        switch (status) {
            case PERSISTENT_FAIL: return 'PERSISTENT_FAIL';
            case OCCASIONAL_FAIL: return 'OCCASIONAL_FAIL';
            default: return 'DEFAULT';
        }
    }
}
